/**
 * Product promo area / cart discount hint — keep them in step with the cart.
 *
 * Both are rendered server-side (inc/product-promo.php). When the cart changes
 * without a page load, ask `wc-ajax=niamito_product_promo` for a fresh copy and
 * swap it in. The URL comes from the element itself: it carries the WPML
 * language prefix, so the reply is in the page language.
 *
 * Refreshes are triggered by:
 *   - `data-niamito-promo-geo` on load — the page was rendered without the
 *     visitor's IP (it may be a cached copy), and the endpoint geolocates
 *   - `niamito_added_to_cart` — single product AJAX add (singleProductAddToCart.ts)
 *   - `added_to_cart` / `removed_from_cart` / `wc_fragments_refreshed` — archive
 *     adds, the side cart and other fragment refreshes
 *   - the Cart block's `wc/store/cart` data store — quantity steppers, removals,
 *     coupons and the shipping calculator on the cart page
 */

const PROMO_SELECTOR = "[data-niamito-promo]";

let promoRefreshTimer: number | undefined;
let promoRefreshAbort: AbortController | undefined;
let promoLiveRegion: HTMLElement | undefined;

/**
 * Reads a changed discount hint out to screen readers. The hint itself cannot
 * be a live region: every refresh replaces it, and a replaced region is not
 * announced. This one stays put.
 */
function announcePromoHint(text: string): void {
  if (!promoLiveRegion) {
    promoLiveRegion = document.createElement("div");
    promoLiveRegion.className = "niamito-product-promo__live";
    promoLiveRegion.setAttribute("role", "status");
    promoLiveRegion.setAttribute("aria-live", "polite");
    document.body.appendChild(promoLiveRegion);
  }
  promoLiveRegion.textContent = text;
}

const promoHintText = (el: Element | null): string =>
  el?.querySelector(".niamito-discount-progress__hint, .niamito-discount-progress__note")?.textContent?.trim() || "";

function refreshPromoElement(el: HTMLElement): Promise<void> {
  const url = el.dataset.niamitoPromoUrl;
  if (!url) return Promise.resolve();

  const params = new URLSearchParams({ context: el.dataset.niamitoPromo || "product" });
  if (el.dataset.niamitoPromoProduct) {
    params.set("product_id", el.dataset.niamitoPromoProduct);
  }

  return fetch(`${url}${url.includes("?") ? "&" : "?"}${params}`, {
    credentials: "same-origin",
    signal: promoRefreshAbort?.signal,
  })
    .then((r) => r.json())
    .then((res) => {
      if (!res?.success || typeof res.data?.html !== "string") return;

      // An empty reply means no card applies any more (say the product went
      // out of stock): keep an empty element so a later change can fill it.
      if (res.data.html === "") {
        el.innerHTML = "";
        return;
      }

      const template = document.createElement("template");
      template.innerHTML = res.data.html.trim();
      const next = template.content.firstElementChild;
      if (!next) return;

      const before = promoHintText(el);
      el.replaceWith(next);
      const after = promoHintText(next);
      if (after && after !== before) announcePromoHint(after);
    })
    .catch(() => {
      // Aborted by a newer refresh, or offline: the current copy stays.
    });
}

function refreshPromos(): void {
  window.clearTimeout(promoRefreshTimer);
  promoRefreshTimer = window.setTimeout(() => {
    promoRefreshAbort?.abort();
    promoRefreshAbort = new AbortController();
    document.querySelectorAll<HTMLElement>(PROMO_SELECTOR).forEach((el) => void refreshPromoElement(el));
  }, 250);
}

function watchPromoCartBlock(): void {
  const data = (window as any).wp?.data;
  if (!data?.select || !data?.subscribe) return;

  // What the promo depends on — lines and quantities, coupons, the total and
  // the shipping destination — so the key moves when those do and not on every
  // store tick. null until the store has loaded the cart.
  const promoCartKey = (): string | null => {
    const cart = data.select("wc/store/cart")?.getCartData?.();
    if (!Array.isArray(cart?.items)) return null;
    const address = cart.shippingAddress || {};
    return [
      cart.items.map((item: { key: string; quantity: number }) => `${item.key}:${item.quantity}`).join(","),
      (cart.coupons || []).map((coupon: { code: string }) => coupon.code).join(","),
      cart.totals?.total_price,
      `${address.country}/${address.state}/${address.postcode}`,
    ].join("|");
  };

  let last = promoCartKey();
  data.subscribe(() => {
    const key = promoCartKey();
    if (key === null || key === last) return;

    // The store loading the cart the page was rendered with is not a change.
    const loaded = last !== null;
    last = key;
    if (loaded) refreshPromos();
  });
}

/**
 * Moves the cart-page promo under the cart's item list, ahead of the totals.
 *
 * The server prints it in front of the Cart block (PHP cannot reach inside the
 * block: its markup is rebuilt by React), and CSS keeps it hidden there. Once
 * the block has rendered its item column, the promo is appended to it. The
 * observer puts it back whenever the block rebuilds that column or a refresh
 * swaps the element.
 *
 * Emptying the cart unmounts the column and the promo with it, so the last
 * element seen is kept and re-appended when a filled cart comes back.
 */
function placeCartPromo(): void {
  const root = document.querySelector(".wp-block-woocommerce-cart");
  if (!root) return;

  let promo = document.querySelector('[data-niamito-promo="cart"]');

  const place = (): void => {
    promo = document.querySelector('[data-niamito-promo="cart"]') || promo;
    const column = root.querySelector(".wc-block-cart__main");
    if (promo && column && promo.parentElement !== column) {
      column.appendChild(promo);
    }
  };

  place();
  new MutationObserver(place).observe(root, { childList: true, subtree: true });
}

document.addEventListener("DOMContentLoaded", () => {
  if (!document.querySelector(PROMO_SELECTOR)) return;

  const $ = (window as any).jQuery;
  if ($) {
    $(document.body).on(
      "niamito_added_to_cart added_to_cart removed_from_cart wc_fragments_refreshed",
      refreshPromos,
    );
  }

  if (document.querySelector('[data-niamito-promo="cart"]')) {
    placeCartPromo();
    watchPromoCartBlock();
  }

  if (document.querySelector("[data-niamito-promo-geo]")) {
    refreshPromos();
  }
});
